
// --- Vue & Template imports ---
import { Component, Vue } from 'vue-property-decorator';

// --- Models ---
import { Customer } from '@/models/Customer.model';
import { EmailAddress } from '@/models/EmailAddress.model';
import { Term } from '@/models/Term.model';

// --- Services ---
import AppService from '@/services/app';
import CustomerService from '@/services/customer';
import EmailAddressService from '@/services/emailAddress';
import TermService from '@/services/term';

// --- Third Party ---
import Hashids from 'hashids';

@Component
export default class CustomerListing extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;
  confirmActionDelay: boolean = false;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  
  clickedItem: Customer = {
    id: 0,
    name: '',
    code: '',
    sendPod: false,
    sendInvoice: false,
    ratesLastUpdatedAt: '',
    suspended: false,
    activeLoadCount: 0,
    approveLoadCount: 0,
  };

  emailItem: EmailAddress = {
    id: 0,
    customerId: 0,
    emailTypeId: 0,
    emailType: '',
    address: '',
  };

  termItem: Term = {
    id: 0,
    customerId: 0,
    term: '',
  };

  isUpdate: boolean = false;
  createUpdateDelay: boolean = false;
  tab: number = 0;
  tabs: string[] = ['Client', 'Email Addresses', 'Terms'];
  title: string = '';
  customerValid: boolean = false;
  accountEmailValid: boolean = false;
  adminEmailValid: boolean = false;
  termsValid: boolean = false;


  adminEmailAddress: string = '';
  adminEmailAddresses: EmailAddress[] = [];
  accountEmailAddress: string = '';
  accountEmailAddresses: EmailAddress[] = [];
  newEmailAddressId: number = -1;

  terms: Term[] = [];
  newTermId: number = -1;

  // used by Confirm Action Dialog for Activate/Suspend/Delete Item
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    email: (value: string) => /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(value) || 'E-mail must be valid',
  };

  // region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Code',
          value: 'code',
        },
        {
          text: 'Name',
          value: 'name',
        },
        {
          text: 'Send POD',
          value: 'sendPod',
        },
        {
          text: 'Send Invoice',
          value: 'sendInvoice',
        },
        {
          text: 'Rates Last Updated At',
          value: 'ratesLastUpdatedAt',
        },
        {
          text: 'Active Loads',
          value: 'activeLoadCount',
        },
        {
          text: 'Approval Loads',
          value: 'approveLoadCount',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    emailHeaders() {
      return [
        {
          text: '',
          value: 'address',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    termHeaders() {
      return [
        {
          text: '',
          value: 'term',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await EmailAddressService.listTypes(false);
      await EmailAddressService.listAll(false);
      await TermService.listAll(false);
      await this.listAll();
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await CustomerService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    redirect(pageName: string, item: Customer) {
      this.$router.push({ name: pageName, params: { id: '' + this.hashids.encode(item.id) } } ); 
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowRouteRateRedirectFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 3},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowSuspendFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    getEmailAddresses() {
      return this.adminEmailAddresses.concat(this.accountEmailAddresses);
    }

    async launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        name: '',
        sendPod: false,
        code: '',
        sendInvoice: false,
        ratesLastUpdatedAt: '',
        suspended: false,
        activeLoadCount: 0,
        approveLoadCount: 0,
      };

      this.adminEmailAddresses = [];
      this.accountEmailAddresses = [];
      this.terms = [];

      this.tab = 0;
      this.title = 'Create New Client';

      this.isUpdate = false;
      this.showItemDialog = true;

      /*
      // check if nexttick() will replace the promise delay

      // Create a new Promise and resolve after 0.5 seconds
      const myTimerPromise = await new Promise((resolve, reject) => {
          setTimeout(resolve, 50);  // "resolve" is already a function, no need for another anonymous function here
      });

      // since we are always openening on tab 0 we only need to reset validation on the first page on new dialog
      // am resetting validation of the customerForm below @EstateWizard_goNext()
      if (this.$refs.customerForm) {
        (this.$refs.customerForm as any).resetValidation();
      }
      */
    }

    async launchUpdateItemDialog(item: Customer) {
      this.clickedItem = Object.assign({}, item);
      this.title = 'Update Client';
      this.tab = 0;
      this.isUpdate = true;

      // making copies of these arrays because we are allowing the user to make changes to the data and we still need the 
      // orig data if they don't save it

      const emailAddresses: EmailAddress[] = Array.from(this.$store.getters['emailAddresses/emailAddressesByCustomer'](this.clickedItem.id));
      this.adminEmailAddresses = emailAddresses.filter((item: EmailAddress) => item.emailTypeId === 1 );
      this.accountEmailAddresses = emailAddresses.filter((item: EmailAddress) => item.emailTypeId === 2 );

      this.terms = Array.from(this.$store.getters['terms/termsByCustomer'](this.clickedItem.id));

      this.showItemDialog = true; 
      
      /*
      // check if nexttick() will replace the promise delay

      // Create a new Promise and resolve after 0.5 seconds
      const myTimerPromise = await new Promise((resolve, reject) => {
          setTimeout(resolve, 50);  // "resolve" is already a function, no need for another anonymous function here
      });

      // since we are always openening on tab 0 we only need to reset validation on the first page on new dialog
      // am resetting validation of the customerForm below @EstateWizard_goNext()
      if (this.$refs.customerForm) {
        (this.$refs.customerForm as any).resetValidation();
      }
      */
    }

    get availableSteps() {
      const steps = [0];

      if (this.customerValid) {
        steps.push(1);
        steps.push(2);
        steps.push(3);
      }

      return steps;
    }

    get scope() {
      if (this.tab === 0) {
        return 'client';
      } else if (this.tab === 1) {
        return 'email';
      } else {
        return 'terms';
      }
    }

    CustomerWizard_cancel() {
      if (this.tab > 0 && !this.isUpdate) {
        this.launchConfirmCloseWizardDialog();
      } else {
        this.showItemDialog = false;
      }
    }

    async CustomerWizard_goNext() {
      if (this.scope === 'client') {
        if (this.isUpdate && this.customerDetailsChanged()) {
          // update the customer details
          await this.doCustomerDetailUpdate();
        }
        this.tab++;
      } else if (this.scope === 'email') {
        if (this.isUpdate && this.emailDetailsChanged()) {
          // update the customer emails
          await this.doEmailUpdate();
        }
        this.tab++;
      } else if (this.scope === 'terms') {

        if (this.isUpdate) {
          if (this.termsChanged()) {
            // update the customer terms
            await this.doTermUpdate();
          } else {
            // just close the dialog, nothing has been changed
            this.showItemDialog = false;
          }
        } else {
          // save the whole shebang
          await this.doCreateNewCustomer();
        }
      }
    }

    customerDetailsChanged() {
      // check if the clickedItem still matches the item in the listing
      const origCustomerDetails: Customer = this.$store.getters['customers/customers'].find((item: Customer) => item.id === this.clickedItem.id);

      return !(origCustomerDetails.name === this.clickedItem.name &&
        origCustomerDetails.code === this.clickedItem.code &&
        origCustomerDetails.sendPod === this.clickedItem.sendPod &&
        origCustomerDetails.sendInvoice === this.clickedItem.sendInvoice);
    }

    emailDetailsChanged() {
      const origCustomerEmailAddresses: EmailAddress[] = this.$store.getters['emailAddresses/emailAddressesByCustomer'](this.clickedItem.id);

      // check the lengths of the email lists
      const emailAddresses = this.getEmailAddresses();
      if (origCustomerEmailAddresses.length === emailAddresses.length) {
        let changed: boolean = false;
        // make sure all the orig email addresses are in the current list
        for (let i = 0; i < origCustomerEmailAddresses.length && !changed; i++) {
          const email = emailAddresses.find((item: EmailAddress) => item.id === origCustomerEmailAddresses[i].id);
          if (!email) {
            // we couldn't find this orig email address id in the current list, so our list has changed
            changed = true;
          }
        }
        return changed;
      } else {
        return true;
      }
    }

    termsChanged() {
      const origCustomerTerms: Term[] = this.$store.getters['terms/termsByCustomer'](this.clickedItem.id);

      // check the lengths of the terms lists
      if (origCustomerTerms.length === this.terms.length) {
        let changed: boolean = false;
        // make sure all the orig terms are in the current list
        for (let i = 0; i < origCustomerTerms.length && !changed; i++) {
          const term = this.terms.find((item: Term) => item.id === origCustomerTerms[i].id);
          if (!term) {
            // we couldn't find this orig term id in the current list, so our list has changed
            changed = true;
          }
        }
        return changed;
      } else {
        return true;
      }
    }

    async doCustomerDetailUpdate() {
      try {
        this.createUpdateDelay = true;
        this.confirmActionDelay = true;

        const response = await CustomerService.update({ 
          id: this.clickedItem.id,
          code: this.clickedItem.code, 
          name: this.clickedItem.name,
          sendPod: this.clickedItem.sendPod,
          sendInvoice: this.clickedItem.sendInvoice,
          suspended: this.clickedItem.suspended,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            // do nothing
            AppService.errorHandler(response.data.message);
          } else {
            // we have replies!
            // Update the Customer's information
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          AppService.logSupportDebug('Customers.vue - doCustomerDetailUpdate - 1006 - ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.createUpdateDelay = false;
        this.confirmActionDelay = false;
      }
    }

    addAdminEmail() {
      this.emailItem.address = this.adminEmailAddress;
      this.emailItem.emailTypeId = 1; //1: Admin

      // check the email address doesn't already exist for this email type before we can add
      const emailAlreadyExists = this.adminEmailAddresses.findIndex((item: EmailAddress) => item.address === this.emailItem.address);

      if (emailAlreadyExists >= 0) {
        AppService.errorHandler('This Email Address has already been added');
      } else {
        this.emailItem.id = this.newEmailAddressId--;
        this.emailItem.address = this.adminEmailAddress;
        this.emailItem.emailTypeId = 1; //1: Admin
        this.adminEmailAddresses.push(Object.assign({}, this.emailItem));
        this.adminEmailAddress = '';

        this.emailItem = {
          id: 0,
          customerId: 0,
          emailTypeId: 0,
          emailType: '',
          address: '',
        };

        this.$nextTick(() => {
          if (this.$refs.adminEmailForm) {
            ((this.$refs.adminEmailForm) as any).resetValidation();
          }
        });
      }
    }

    addAccountEmail() {
      this.emailItem.address = this.accountEmailAddress;
      this.emailItem.emailTypeId = 2; //2: Account

      // check the email address doesn't already exist for this email type before we can add
      const emailAlreadyExists = this.accountEmailAddresses.findIndex((item: EmailAddress) => item.address === this.emailItem.address);

      if (emailAlreadyExists >= 0) {
        AppService.errorHandler('This Email Address has already been added');
      } else {
        this.emailItem.id = this.newEmailAddressId--;
        this.emailItem.address = this.accountEmailAddress;
        this.emailItem.emailTypeId = 2; //2: Account
        this.accountEmailAddresses.push(Object.assign({}, this.emailItem));
        this.accountEmailAddress = '';

        this.emailItem = {
          id: 0,
          customerId: 0,
          emailTypeId: 0,
          emailType: '',
          address: '',
        };

        this.$nextTick(() => {
          if (this.$refs.accountEmailForm) {
            ((this.$refs.accountEmailForm) as any).resetValidation();
          }
        });
      }
    }

    deleteAdminEmailAddress(item: EmailAddress) {
      const index = this.adminEmailAddresses.findIndex((a: EmailAddress) => a.id === item.id);
      this.adminEmailAddresses.splice(index, 1);
    }

    deleteAccountEmailAddress(item: EmailAddress) {
      const index = this.accountEmailAddresses.findIndex((a: EmailAddress) => a.id === item.id);
      this.accountEmailAddresses.splice(index, 1);
    }

    async doEmailUpdate() {
      try {
        this.createUpdateDelay = true;

        const response = await EmailAddressService.update({ 
          customerId: this.clickedItem.id, 
          emailAddresses: this.getEmailAddresses(),
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await EmailAddressService.listAll(true);
            await this.listAll();
          } 
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('Customers.vue - doEmailUpdate - 1123 - ' + error);
        }
      } finally {
        this.createUpdateDelay = false;
      }
    }

    clearTermItem() {
      this.termItem = {
        id: 0,
        customerId: 0,
        term: '',
      };

      this.$nextTick(() => {
        if (this.$refs.termForm) {
          ((this.$refs.termForm) as any).resetValidation();
        }
      });
    }

    addTerm() {
      // check the term doesn't already exist before we can add
      const termAlreadyExists = this.terms.findIndex((item: Term) => 
        item.term === this.termItem.term);

      if (termAlreadyExists >= 0) {
        AppService.errorHandler('This Term has already been added');
      } else {
        this.termItem.id = this.newTermId--;
        this.terms.push(Object.assign({}, this.termItem));
        this.clearTermItem();
      }
    }

    deleteTerm(item: Term) {
      const index = this.terms.findIndex((a: Term) => a.id === item.id);
      this.terms.splice(index, 1);
    }

    async doTermUpdate() {
      try {
        this.createUpdateDelay = true;

        const response = await TermService.update({ 
          customerId: this.clickedItem.id, 
          terms: this.terms,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await TermService.listAll(true);
            await this.listAll();
            this.showItemDialog = false;
          } 
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('Customers.vue - doTermUpdate - 1190 - ' + error);
        }
      } finally {
        this.createUpdateDelay = false;
      }
    }

    async doCreateNewCustomer() {
      try {
        this.createUpdateDelay = true;

        const response = await CustomerService.create( {
          name: this.clickedItem.name,
          code: this.clickedItem.code, 
          sendInvoice: this.clickedItem.sendInvoice,
          sendPod: this.clickedItem.sendPod,
          emailAddresses: this.getEmailAddresses(),
          terms: this.terms,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await EmailAddressService.listAll(true);
            await TermService.listAll(true);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          AppService.logSupportDebug('Customers.vue - doCreateNewCustomer - 1222 - ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Customers.vue - doCreateNewCustomer - 1230 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.createUpdateDelay = false;
      }
    }
    
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Suspend/Activate/Delete
    launchActivateItemDialog(item: Customer) {
      this.clickedItem = item;

      this.action = 'ACTIVATE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #71BC78">re-activate</span> the Client named <span style="font-weight: 700">' 
        + item.name + '</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchSuspendItemDialog(item: Customer) {
      this.clickedItem = item;

      this.action = 'SUSPEND';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212 ">suspend</span> the Client named <span style="font-weight: 700">' 
        + item.name + '</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    async launchDeleteItemDialog(item: Customer) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> ' + this.clickedItem.name + '<br/><br/> Are you sure? ';
      this.showConfirmActionDialog = true;
    }

    launchConfirmCloseWizardDialog() {
      this.action = 'CONFIRM_CANCEL';
      this.dialogContent = 'If you close this window, you will lose any Client Information that you have entered on previous steps.<br/><br/> Are you sure?';

      this.showItemDialog = false;
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      if (this.action === 'CONFIRM_CANCEL') {
         this.showItemDialog = true;
      }
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'SUSPEND') { 
        this.clickedItem.suspended = true;
        await this.doCustomerDetailUpdate();

      } else if (this.action === 'ACTIVATE') {
        this.clickedItem.suspended = false;
        await this.doCustomerDetailUpdate();
        
      } else if (this.action === 'CONFIRM_CANCEL') {
        // do nothing more, the showItemDialog has already been hidden
        this.showConfirmActionDialog = false;
      } else if (this.action === 'DELETE') {
        await this.doDelete();        
      }
    }

     async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await CustomerService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Customers.vue - doDelete - 1327 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion

}
